import { EntityId } from "@jackfruit/common"
import { SagaIterator } from "@redux-saga/types"
import { nanoid, PayloadAction } from "@reduxjs/toolkit"
import { call, put, select, takeEvery } from "redux-saga/effects"
import { ImageRegionEntity } from "~/interfaces/entities/ImageRegion"
import { actions, UpdateImageRegionImage } from "../process"
import { imageRegions, imageRegionsSelectors } from "../state/imageRegions"
import { uploads } from "../state/uploads"
import { RootState } from "../store"
import {
  convertImageToJpegIFNecessary,
  createImage,
  setImageTransformation,
} from "./images"
import { attachUploadToPageSession } from "./pageSession"

export default function* watchUpdateImageRegionImage() {
  yield takeEvery(
    actions.updateImageRegionImage.type,
    processUpdateImageRegionImage
  )
}

export function* processUpdateImageRegionImage(
  action: PayloadAction<UpdateImageRegionImage>
): SagaIterator<void> {
  const {
    imageRegionId,
    file,
    isClampingEnabled = true,
    imageFilterName,
  } = action.payload
  const { addOne: addOneUpload } = uploads.actions
  const { updateOne: updateOneImageRegion } = imageRegions.actions

  const uploadId = nanoid()

  const imageRegion: ImageRegionEntity = yield select((state: RootState) =>
    imageRegionsSelectors.selectById(state, imageRegionId)
  )

  const convertedFile = yield call(convertImageToJpegIFNecessary, file)

  const imageId: EntityId = yield call(createImage, { file: convertedFile })

  yield call(setImageTransformation, {
    imageId: imageId,
    window: imageRegion.window,
    imageFit: imageRegion.imageFitMode,
    isClampingEnabled,
    imageFilterName,
  })

  yield put(
    addOneUpload({
      id: uploadId,
      imageId: imageId,
      name: convertedFile.name,
      hash: "",
      type: convertedFile.type,
      size: convertedFile.size,
      progress: 0,
      status: "queued",
      isReady: false,
      hasFailed: false,
      error: null,
    })
  )

  // attach new file to line item
  yield put(
    updateOneImageRegion({
      id: imageRegion.id,
      changes: {
        uploadId,
      },
    })
  )

  // attach upload to page session
  yield call(attachUploadToPageSession, uploadId)

  // queue upload for processing
  yield put(
    actions.queueImageForProcessing({
      id: uploadId,
      file: convertedFile,
    })
  )
}
